// FindSideFromTwoSidesAndIncludedAngleTask.js
// Dane: boki a, b oraz kąt γ między nimi. Oblicz długość boku c.
// W przygotowaniu utrwalamy maturalny wariant z kątem 60°.
// Wzór: c^2 = a^2 + b^2 - 2ab cos γ

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseRootFractionInput,
} from "../../../../components/inputs/FractionInput";

/* ===== helpers ===== */

const randInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));

function formatDecimalLatex(x, digits = 2) {
  const s = x.toFixed(digits);
  const trimmed = s.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return trimmed.replace(".", "{,}");
}

function cosLatex(deg) {
  switch (deg) {
    case 30:  return "\\tfrac{\\sqrt{3}}{2}";
    case 45:  return "\\tfrac{\\sqrt{2}}{2}";
    case 60:  return "\\tfrac{1}{2}";
    case 120: return "-\\tfrac{1}{2}";
    case 135: return "-\\tfrac{\\sqrt{2}}{2}";
    case 150: return "-\\tfrac{\\sqrt{3}}{2}";
    default:  return "";
  }
}
function cosNumeric(deg) {
  switch (deg) {
    case 30:  return Math.sqrt(3) / 2;
    case 45:  return Math.SQRT2 / 2;
    case 60:  return 0.5;
    case 120: return -0.5;
    case 135: return -Math.SQRT2 / 2;
    case 150: return -Math.sqrt(3) / 2;
    default:  return Math.cos((deg * Math.PI) / 180);
  }
}
function twoCosLatex(deg) {
  switch (deg) {
    case 60:  return "1";
    case 120: return "-1";
    case 45:  return "\\sqrt{2}";
    case 135: return "-\\sqrt{2}";
    case 30:  return "\\sqrt{3}";
    case 150: return "-\\sqrt{3}";
    default:  return "";
  }
}

function preprocessRaw(raw) {
  if (raw == null) return "";
  return String(raw).trim()
    .replace(/\u2212/g, "-")
    .replace(/\s+/g, "")
    .replace(",", ".");
}

/* ===== generator ===== */

export function generateTask() {
  const gamma = 60;
  const a = randInt(3, 15);
  const b = Math.random() < 0.4 ? a : randInt(3, 15);

  const expected = Math.sqrt(a * a + b * b - 2 * a * b * cosNumeric(gamma));

  return {
    type: "FindSideFromTwoSidesAndIncludedAngleTask",
    data: { type: "FindSideFromTwoSidesAndIncludedAngleTask", a, b, gamma, expected },
  };
}

/* ===== treść ===== */

export function renderTask({ data }) {
  const { a, b, gamma } = data;
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        W trójkącie <InlineMath math={'ABC'} /> dane są boki{" "}
        <InlineMath math={`a=${a}`} /> i <InlineMath math={`b=${b}`} /> oraz kąt{" "}
        <InlineMath math={`\\gamma=${gamma}^\\circ`} /> między nimi.
        Oblicz długość <strong>boku</strong> <InlineMath math={'c'} />.
      </div>
      <div style={{ marginTop: 6, opacity: 0.9 }}>
        Podaj wynik z dokładnością do <InlineMath math={'0{,}01'} />.
      </div>
    </div>
  );
}

/* ===== input ===== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 21,34"
        label="c"           // <-- zamiast domyślnego "a"
      />
    </div>
  );
}

/* ===== check ===== */

export function checkAnswer({ data }, userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || String(raw).trim() === "") return false;

  const val = parseRootFractionInput(preprocessRaw(raw));
  if (val == null || !Number.isFinite(val)) return false;

  const exp = Number(data.expected);
  const tol = 0.01; // dokładność do 0,01
  return Math.abs(val - exp) <= tol;
}

/* ===== answer ===== */

export function renderAnswer({ data }) {
  const { expected } = data;
  const rounded = Math.round(expected * 100) / 100;
  const isExactInt = Math.abs(expected - Math.round(expected)) < 1e-9;

  const latex =
    isExactInt
      ? `c = ${Math.round(expected)}`
      : `c \\approx ${formatDecimalLatex(rounded, 2)}`;

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={latex} />
    </div>
  );
}

/* ===== explanation ===== */

export function renderExplanation({ data }) {
  const { a, b, gamma, expected } = data;

  const A2 = a * a;
  const B2 = b * b;
  const AB = a * b;
  const sum = A2 + B2;

  const cosL   = cosLatex(gamma);     // np. -\tfrac{\sqrt{2}}{2}
  const twoCos = twoCosLatex(gamma);  // np. -\sqrt{2}
  const absFac = twoCos.replace(/^-/, '');
  const op     = twoCos.startsWith('-') ? '+' : '-';

  const radicandStep =
    absFac === '1'
      ? `${sum} ${op} ${AB} = ${op === '+' ? sum + AB : sum - AB}`
      : `${sum} ${op} ${AB}\\,${absFac}`;

  const rounded = Math.round(expected * 100) / 100;
  const isExactInt = Math.abs(expected - Math.round(expected)) < 1e-9;
  const finalLatex = isExactInt
    ? `c = ${Math.round(expected)}`
    : `c \\approx ${formatDecimalLatex(rounded, 2)}`;

  // --- redukcja dla kąta rozwart. γ > 90°
  const showReduction = gamma > 90;
  const acute = 180 - gamma;            // kąt ostry do redukcji
  const cosAcute = cosLatex(acute);     // dodatnia wartość kosinusa dla kąta ostrego

  return (
    <div style={{ textAlign: 'left', marginTop: 8, lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Twierdzenie cosinusów:</div>
      <BlockMath math={'c^{2} = a^{2} + b^{2} - 2ab\\cos\\gamma'} />

      <div><strong>Krok 2.</strong> Podstawiamy dane:</div>
      <BlockMath math={`c^{2} = ${a}^{2} + ${b}^{2} - 2\\cdot ${a}\\cdot ${b}\\cdot \\cos ${gamma}^\\circ`} />

      <div><strong>Krok 3.</strong> Liczymy kwadraty i iloczyn:</div>
      <BlockMath math={`a^{2} = ${A2},\\quad b^{2} = ${B2},\\quad ab = ${AB}`} />
      <BlockMath math={`\\Rightarrow\\; a^{2} + b^{2} = ${A2} + ${B2} = ${sum}`} />

      {showReduction && (
        <>
          <div>
            <strong>Krok 4.</strong> Redukcja kąta (
            <InlineMath math={'\\gamma > 90^\\circ'} />
            ):
          </div>
          <BlockMath math={`\\cos ${gamma}^\\circ = \\cos\\big(180^\\circ - ${acute}^\\circ\\big)`} />
          <BlockMath math={`= -\\cos ${acute}^\\circ`} />
          <BlockMath math={`= -${cosAcute}`} />
        </>
      )}

      <div><strong>{showReduction ? 'Krok 5.' : 'Krok 4.'}</strong> Wartość kosinusa:</div>
      <BlockMath math={`\\cos ${gamma}^\\circ = ${cosL}`} />
      <BlockMath math={`2\\cos ${gamma}^\\circ = ${twoCos}`} />

      <div><strong>{showReduction ? 'Krok 6.' : 'Krok 5.'}</strong> Składamy i upraszczamy wyrażenie pod pierwiastkiem:</div>
      <BlockMath math={`c^{2} = ${sum} - ${AB}\\,\\big(2\\cos ${gamma}^\\circ\\big)`} />
      <BlockMath math={`c^{2} = ${radicandStep}`} />

      <div style={{ marginTop: 6 }}>
        <strong>Odpowiedź:</strong>{' '}
        <BlockMath math={finalLatex} />
      </div>
    </div>
  );
}

/* ===== user answer preview ===== */

function prettyLatex(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (/^[+-]?\d+[.,]\d+$/.test(s.replace(/\s+/g, ""))) {
    return s.replace(/\s+/g, "").replace(".", "{,}");
  }
  return s;
}

export function renderUserAnswer(userAnswer) {
  const raw =
    typeof userAnswer === "object" ? (userAnswer.raw ?? userAnswer.value) : userAnswer;

  if (!raw || String(raw).trim() === "") return <i>(brak odpowiedzi)</i>;

  const pretty = prettyLatex(raw); // zamienia 21.34 -> 21{,}34 itd.

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`\\displaystyle c = ${pretty || '?'}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
